import { Fragment, useLayoutEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProposalExtraItemType, ProposalProductItemType } from '@econtractor/types';
import { formatPrice } from 'utils/helpers/functions/formatPrice';
import type { ProposalEditFormValues } from '../Proposal/types';
import {
    getProposalDisplayPrice,
    getProposalLineTotal,
    getProposalLineVat,
    getProposalUseUsd,
} from './model/helper/proposalPriceUtils';
import { stripPreviewHtmlColors } from './model/helper/stripPreviewHtmlColors';
import {
    buildContentBlocks,
    buildMarkedSectionBlocks,
    getPageMetrics,
    measurePreviewBlocks,
    packBlocksIntoPages,
    type ContentPage,
    type MarkedSectionTableParams,
    type PreviewBlock,
    type PreviewRow,
} from './model/helper/proposalPreviewPagination';
import { hasTemplateTableMarker } from './model/helper/templateMarkers';
import useTemplateValueKeys from './model/useTemplateValueKeys';
import { buildObjectTemplateValueKeysMap } from './model/buildObjectTemplateValueKeys';
import { buildDocumentTemplateValueKeysMap } from './model/buildDocumentTemplateValueKeys';
import { hasHtmlContent } from 'utils/helpers/functions/stripHtml';
import {
    getBaseCurrencyCode,
    getCurrencyCode,
    getCurrencyI18nParams,
} from 'utils/helpers/currency/currencyHelpers';

interface ProposalPreviewTabProps {
    formData: ProposalEditFormValues;
    rate: number;
    translationNs?: string;
    showTitlePage?: boolean;
    includeOutro?: boolean;
    objectData?: unknown;
}

const mapProductRow = (
    row: ProposalProductItemType,
    index: number,
    rate: number,
    pricesInUsd: boolean,
    withVat: boolean,
): PreviewRow => {
    const price = getProposalDisplayPrice(row, rate, pricesInUsd);
    const total = getProposalLineTotal(row, rate, pricesInUsd);
    return {
        rowId: row.rowId || `product-${row.productId}-${index}`,
        name: row.prodName || row.sourceName || `#${row.productId}`,
        count: Number(row.itemCount || 0),
        price,
        total,
        vat: getProposalLineVat(row, rate, pricesInUsd, withVat),
    };
};

const mapAdditionalRow = (
    row: ProposalExtraItemType,
    index: number,
    rate: number,
    pricesInUsd: boolean,
    withVat: boolean,
): PreviewRow => {
    const price = getProposalDisplayPrice(row, rate, pricesInUsd);
    const total = getProposalLineTotal(row, rate, pricesInUsd);
    return {
        rowId: row.rowId || `additional-${index}`,
        name: row.itemName || '-',
        count: Number(row.itemCount || 0),
        price,
        total,
        vat: getProposalLineVat(row, rate, pricesInUsd, withVat),
    };
};

const renderTableChunk = (
    blocks: PreviewBlock[],
    currencyLabel: string,
    withVat: boolean,
    labels: {
        tableName: string;
        tableCount: string;
        tablePrice: string;
        tableTotal: string;
        tableVat: string;
    },
) => {
    const header = blocks.find((b) => b.kind === 'table-header');
    const rows = blocks.filter((b) => b.kind === 'table-row') as Extract<
        PreviewBlock,
        { kind: 'table-row' }
    >[];
    const footer = blocks.find((b) => b.kind === 'table-footer') as
        | Extract<PreviewBlock, { kind: 'table-footer' }>
        | undefined;

    if (!header && !rows.length && !footer) return null;

    return (
        <table className="proposal-preview-table">
            {header ? (
                <thead>
                    <tr>
                        <th>{labels.tableName}</th>
                        <th className="proposal-preview-col-num">{labels.tableCount}</th>
                        <th className="proposal-preview-col-num">
                            {labels.tablePrice} {currencyLabel}
                        </th>
                        <th className="proposal-preview-col-num">
                            {labels.tableTotal} {currencyLabel}
                        </th>
                        {withVat ? (
                            <th className="proposal-preview-col-num">
                                {labels.tableVat} {currencyLabel}
                            </th>
                        ) : null}
                    </tr>
                </thead>
            ) : null}
            {rows.length ? (
                <tbody>
                    {rows.map((block) => (
                        <tr key={block.id}>
                            <td>{block.row.name}</td>
                            <td className="proposal-preview-col-num">{formatPrice(block.row.count)}</td>
                            <td className="proposal-preview-col-num">{formatPrice(block.row.price)}</td>
                            <td className="proposal-preview-col-num">{formatPrice(block.row.total)}</td>
                            {withVat ? (
                                <td className="proposal-preview-col-num">
                                    {formatPrice(block.row.vat || 0)}
                                </td>
                            ) : null}
                        </tr>
                    ))}
                </tbody>
            ) : null}
            {footer ? (
                <tfoot>
                    <tr>
                        <td colSpan={3} className="proposal-preview-total-label">
                            {footer.totalLabel}
                        </td>
                        <td className="proposal-preview-col-num proposal-preview-total-value">
                            {footer.totalValue}
                        </td>
                        {withVat ? (
                            <td className="proposal-preview-col-num proposal-preview-total-value">
                                {footer.vatValue || formatPrice(0)}
                            </td>
                        ) : null}
                    </tr>
                </tfoot>
            ) : null}
        </table>
    );
};

const renderPageBlocks = (
    page: ContentPage,
    currencyLabel: string,
    withVat: boolean,
    labels: {
        tableName: string;
        tableCount: string;
        tablePrice: string;
        tableTotal: string;
        tableVat: string;
    },
) => {
    const nodes: ReactNode[] = [];
    let i = 0;

    while (i < page.blocks.length) {
        const block = page.blocks[i];

        if (
            block.kind === 'table-header' ||
            block.kind === 'table-row' ||
            block.kind === 'table-footer'
        ) {
            const chunk: PreviewBlock[] = [];
            while (
                i < page.blocks.length &&
                (page.blocks[i].kind === 'table-header' ||
                    page.blocks[i].kind === 'table-row' ||
                    page.blocks[i].kind === 'table-footer')
            ) {
                chunk.push(page.blocks[i]);
                i += 1;
            }
            nodes.push(
                <Fragment key={`table-chunk-${chunk[0]?.id || i}`}>
                    {renderTableChunk(chunk, currencyLabel, withVat, labels)}
                </Fragment>,
            );
            continue;
        }

        if (block.kind === 'html') {
            nodes.push(
                <div
                    key={block.id}
                    className="proposal-preview-rich-text"
                    dangerouslySetInnerHTML={{ __html: block.html }}
                />,
            );
        } else if (block.kind === 'section-title') {
            nodes.push(
                <h2 key={block.id} className="proposal-preview-section-title">
                    {block.text}
                </h2>,
            );
        } else if (block.kind === 'empty') {
            nodes.push(
                <p key={block.id} className="proposal-preview-empty">
                    {block.text}
                </p>,
            );
        }
        i += 1;
    }

    return nodes;
};

const ProposalPreviewTab = ({
    formData,
    rate,
    translationNs = 'Proposal',
    showTitlePage = false,
    includeOutro = true,
    objectData,
}: ProposalPreviewTabProps) => {
    const { t } = useTranslation(translationNs);
    const { t: tObjects } = useTranslation('Objects');
    const { buildTemplateValueKeysMap, replaceTemplateValueKeys } = useTemplateValueKeys();
    const useUsd = getProposalUseUsd(formData.pricesInUsd, rate);
    const withVat = Boolean(formData.proposalVat);
    const currencyLabel = useUsd
        ? t('edit.preview.currencyUsd', getCurrencyI18nParams())
        : t('edit.preview.currencyUah', getCurrencyI18nParams());

    const tableLabels = useMemo(
        () => ({
            tableName: t('edit.preview.tableName'),
            tableCount: t('edit.preview.tableCount'),
            tablePrice: t('edit.preview.tablePrice'),
            tableTotal: t('edit.preview.tableTotal'),
            tableVat: t('edit.preview.tableVat'),
        }),
        [t],
    );

    const productsRows = useMemo(
        () =>
            (formData.products || []).map((row, index) =>
                mapProductRow(row, index, rate, useUsd, withVat),
            ),
        [formData.products, rate, useUsd, withVat],
    );
    const additionalsRows = useMemo(
        () =>
            (formData.additionalItems || []).map((row, index) =>
                mapAdditionalRow(row, index, rate, useUsd, withVat),
            ),
        [formData.additionalItems, rate, useUsd, withVat],
    );
    const specificationRows = useMemo(
        () => [...productsRows, ...additionalsRows],
        [productsRows, additionalsRows],
    );
    const sumRows = (rows: PreviewRow[]) =>
        rows.reduce(
            (acc, row) => ({
                total: acc.total + Number(row.total || 0),
                vat: acc.vat + Number(row.vat || 0),
            }),
            { total: 0, vat: 0 },
        );
    const productsSummary = useMemo(() => sumRows(productsRows), [productsRows]);
    const additionalsSummary = useMemo(() => sumRows(additionalsRows), [additionalsRows]);
    const specificationSummary = useMemo(() => sumRows(specificationRows), [specificationRows]);
    const totalLabel = `${withVat ? t('edit.preview.totalWithVatLabel') : t('edit.preview.totalLabel')} ${currencyLabel}`;
    const valueKeysMap = useMemo(() => {
        const amountKeys = buildTemplateValueKeysMap({
            productsAmount: productsSummary.total,
            additionalsAmount: additionalsSummary.total,
            specificationAmount: specificationSummary.total,
            productsAmountVat: productsSummary.vat,
            additionalsAmountVat: additionalsSummary.vat,
            specificationAmountVat: specificationSummary.vat,
            currencyCode: useUsd ? getBaseCurrencyCode() : getCurrencyCode(),
        });
        const objectKeys = buildObjectTemplateValueKeysMap({
            objectData,
            tObjects: tObjects as never,
        });
        const documentKeys = buildDocumentTemplateValueKeysMap({
            proposalNumber: formData.proposalNumber,
            proposalDt: formData.proposalDt,
        });
        return { ...objectKeys, ...documentKeys, ...amountKeys };
    }, [
        buildTemplateValueKeysMap,
        productsSummary,
        additionalsSummary,
        specificationSummary,
        useUsd,
        objectData,
        tObjects,
        formData.proposalNumber,
        formData.proposalDt,
    ]);
    const titleHtml = useMemo(
        () =>
            stripPreviewHtmlColors(
                replaceTemplateValueKeys(formData.proposalTitle || '', valueKeysMap),
            ),
        [formData.proposalTitle, replaceTemplateValueKeys, valueKeysMap],
    );
    const introHtml = useMemo(
        () =>
            stripPreviewHtmlColors(
                replaceTemplateValueKeys(formData.proposalIntro || '', valueKeysMap),
            ),
        [formData.proposalIntro, replaceTemplateValueKeys, valueKeysMap],
    );
    const outroHtml = useMemo(() => {
        if (!includeOutro) return '';
        return stripPreviewHtmlColors(
            replaceTemplateValueKeys(formData.proposalOutro || '', valueKeysMap),
        );
    }, [formData.proposalOutro, includeOutro, replaceTemplateValueKeys, valueKeysMap]);

    const tableSectionParams = useMemo<MarkedSectionTableParams>(
        () => ({
            specificationRows,
            productsRows,
            additionalsRows,
            specificationTotalLabel: totalLabel,
            specificationTotalValue: formatPrice(specificationSummary.total),
            specificationVatValue: withVat ? formatPrice(specificationSummary.vat) : undefined,
            productsTotalLabel: totalLabel,
            productsTotalValue: formatPrice(productsSummary.total),
            productsVatValue: withVat ? formatPrice(productsSummary.vat) : undefined,
            additionalsTotalLabel: totalLabel,
            additionalsTotalValue: formatPrice(additionalsSummary.total),
            additionalsVatValue: withVat ? formatPrice(additionalsSummary.vat) : undefined,
        }),
        [
            specificationRows,
            productsRows,
            additionalsRows,
            totalLabel,
            specificationSummary,
            productsSummary,
            additionalsSummary,
            withVat,
        ],
    );

    const titleHasMarker = hasTemplateTableMarker(titleHtml);
    const titleBlocks = useMemo(
        () =>
            hasHtmlContent(titleHtml)
                ? buildMarkedSectionBlocks(titleHtml, 'title', tableSectionParams, false)
                : [],
        [titleHtml, tableSectionParams],
    );
    const contentBlocks = useMemo(
        () =>
            buildContentBlocks({
                introHtml,
                outroHtml,
                ...tableSectionParams,
                skipFallbackTable: showTitlePage && titleHasMarker,
            }),
        [introHtml, outroHtml, tableSectionParams, showTitlePage, titleHasMarker],
    );

    const [titlePages, setTitlePages] = useState<ContentPage[]>([]);
    const [contentPages, setContentPages] = useState<ContentPage[]>([
        { id: 'content-page-0', blocks: contentBlocks },
    ]);

    useLayoutEffect(() => {
        const metrics = getPageMetrics();
        const measureLabels = {
            tableName: tableLabels.tableName,
            tableCount: tableLabels.tableCount,
            tablePrice: `${tableLabels.tablePrice} ${currencyLabel}`,
            tableTotal: `${tableLabels.tableTotal} ${currencyLabel}`,
            tableVat: `${tableLabels.tableVat} ${currencyLabel}`,
            withVat,
        };
        const sectionGapPx = Math.ceil((12 * 96) / 72); // 12pt

        if (showTitlePage) {
            if (!titleBlocks.length) {
                setTitlePages([{ id: 'title-page-0', blocks: [] }]);
            } else {
                const titleHeights = measurePreviewBlocks(
                    titleBlocks,
                    measureLabels,
                    metrics.contentWidthPx,
                );
                // Reserve space for logo + «КОМЕРЦІЙНА ПРОПОЗИЦІЯ» on the first title page.
                const titleHeaderReservePx = titleHasMarker ? 160 : 220;
                const packed = packBlocksIntoPages(
                    titleBlocks,
                    titleHeights,
                    Math.max(80, metrics.contentHeightPx - titleHeaderReservePx),
                    sectionGapPx,
                );
                setTitlePages(
                    packed.length
                        ? packed.map((page, index) => ({ ...page, id: `title-page-${index}` }))
                        : [{ id: 'title-page-0', blocks: [] }],
                );
            }
        } else {
            setTitlePages([]);
        }

        const contentHeights = measurePreviewBlocks(
            contentBlocks,
            measureLabels,
            metrics.contentWidthPx,
        );
        setContentPages(
            packBlocksIntoPages(contentBlocks, contentHeights, metrics.contentHeightPx, sectionGapPx),
        );
    }, [
        showTitlePage,
        titleBlocks,
        titleHasMarker,
        contentBlocks,
        tableLabels,
        currencyLabel,
        withVat,
    ]);

    const totalPages = (showTitlePage ? titlePages.length : 0) + contentPages.length;
    const hasBackground = Boolean(String(formData.proposalBackground || '').trim());
    const titlePageClassName = titleHasMarker
        ? 'proposal-preview-a4-page proposal-preview-a4-page--title proposal-preview-a4-page--title-with-table'
        : 'proposal-preview-a4-page proposal-preview-a4-page--title';

    const renderBackground = () =>
        hasBackground ? (
            <div className="proposal-preview-page-background" aria-hidden="true">
                <img
                    src={formData.proposalBackground}
                    alt={t('edit.preview.backgroundAlt')}
                    className="proposal-preview-page-background-image"
                />
                <div className="proposal-preview-page-background-overlay"></div>
            </div>
        ) : null;

    return (
        <div className="proposal-preview-wrap">
            {showTitlePage
                ? titlePages.map((page, index) => (
                      <div
                          key={page.id}
                          className={index === 0 ? titlePageClassName : 'proposal-preview-a4-page'}
                      >
                          {renderBackground()}
                          {index === 0 ? (
                              <div className="proposal-preview-title-content">
                                  {formData.proposalLogo ? (
                                      <img
                                          src={formData.proposalLogo}
                                          alt={t('edit.preview.logoAlt')}
                                          className="proposal-preview-logo"
                                      />
                                  ) : null}
                                  <h1 className="proposal-preview-main-title">
                                      {t('edit.preview.mainTitle')}
                                  </h1>
                                  {page.blocks.length ? (
                                      <div className="proposal-preview-title-body">
                                          {renderPageBlocks(
                                              page,
                                              currencyLabel,
                                              withVat,
                                              tableLabels,
                                          )}
                                      </div>
                                  ) : null}
                              </div>
                          ) : (
                              <div className="proposal-preview-page-body">
                                  {renderPageBlocks(page, currencyLabel, withVat, tableLabels)}
                              </div>
                          )}
                          <div className="proposal-preview-page-number">
                              {index + 1} / {totalPages || 1}
                          </div>
                      </div>
                  ))
                : null}

            {contentPages.map((page, index) => (
                <div key={page.id} className="proposal-preview-a4-page">
                    {renderBackground()}
                    <div className="proposal-preview-page-body">
                        {renderPageBlocks(page, currencyLabel, withVat, tableLabels)}
                    </div>
                    <div className="proposal-preview-page-number">
                        {(showTitlePage ? titlePages.length : 0) + index + 1} / {totalPages || 1}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default ProposalPreviewTab;
